import { StyleSheet, View, ScrollView, TouchableOpacity, Image, ActivityIndicator } from "react-native";
import React, { useState, useEffect } from "react";
import { Ionicons } from "@expo/vector-icons";
import { GroupModel, Contact, Share, MediaModel, ITagAssociationMeta } from "common247";
import { ThemedText } from "../ThemedText";
import { ThemedEntityLink } from "../ThemedEntityLink";
import { ThemedInput } from "../ThemedInput";
import { ThemedButton } from "../ThemedButton";
import { ThemedView } from "../ThemedView";
import { SharingControl } from "../sharing/SharingControl";
import { useAutoSave } from "@/hooks/useAutoSave";
import { AutoSaveIndicator } from "@/components/ui/AutoSaveIndicator";
import { useActiveFieldTracker } from "@/hooks/useActiveField";
import { TagListStrip } from "../tags/TagListStrip";
import { EntityLinkStrip } from "../ui/EntityLinkStrip";
import { useFirebase } from "../firebase/useFirebase";
import { useThemeColor } from "@/hooks/useThemeColor";
import { createProfileImageVariants } from "@/services/image-resizer";
import { createMedia } from "@/services/firebase/media-functions";
import { UploadMedia } from "../media/UploadMedia";
import { CapturePhoto } from "../media/CapturePhoto";
import { MediaSelector } from "../media/MediaSelector";
import { CustomAlert } from "@/components/ThemedAlert";
import { CollaborativeEditor } from "../editor/CollaborativeEditor";
import { ThemedRating } from "../ThemedRating";
import type { IEntityMemoryMeta, IEntityMediaMeta, IEntityPlaceMeta, IEntityAnchorMeta } from "common247";

export interface IGroupEditorProps {
  group?: GroupModel;
  contactsList: Contact[];
  allGroups: GroupModel[];
  shares?: Share[];
  isLoadingShares?: boolean;
  currentUserId?: string;
  onSave: (data: {
    name: string;
    description: string;
    contacts: Record<string, { contactId: string; addedAt: number; addedBy: string; role?: "member" | "admin" }>;
    childGroups: Record<string, { groupId: string; addedAt: number; addedBy: string }>;
    tags?: Record<string, ITagAssociationMeta>;
    photoMediaId?: string;
    favouriteRating?: number;
    memories?: Record<string, IEntityMemoryMeta>;
    media?: Record<string, IEntityMediaMeta>;
    places?: Record<string, IEntityPlaceMeta>;
    anchors?: Record<string, IEntityAnchorMeta>;
  }) => void;
  onShare?: (targetId: string, targetType: "contact" | "group", role: "viewer" | "editor") => void;
  onUnshare?: (shareId: string) => void;
  onCancel: () => void;
  isSaving: boolean;
  submitTitle?: string;
}

export function GroupEditor({
  group,
  contactsList,
  allGroups,
  shares = [],
  isLoadingShares,
  currentUserId,
  onSave,
  onShare,
  onUnshare,
  onCancel,
  isSaving,
  submitTitle = "Save Group",
}: IGroupEditorProps) {
  const [formName, setFormName] = useState(group?.name || "");
  const [formDesc, setFormDesc] = useState(group?.description || "");
  const [photoUrl, setPhotoUrl] = useState(group?.photoUrl || group?.photoIconUrl || "");
  const [photoMediaId, setPhotoMediaId] = useState(group?.photoMediaId || "");
  const [favouriteRating, setFavouriteRating] = useState<number>(group?.favouriteRating ?? 3);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showMediaSelector, setShowMediaSelector] = useState(false);

  const tintColor = useThemeColor({}, "tint");
  const services = useFirebase();
  const resolvedUserId = currentUserId || services?.auth?.currentUser?.uid || "";
  const userRole = group?.userRole || "owner";
  const isOwner = userRole === "owner";
  const isEditor = userRole === "editor";
  const isAllContacts = group?.groupType == "allcontacts";
  const canEdit = (isOwner || isEditor) && !isAllContacts;

  // Member selection
  const [selectedMembers, setSelectedMembers] = useState<{ [contactId: string]: boolean }>(() => {
    const initial: { [contactId: string]: boolean } = {};
    if (group?.contactIds) {
      for (const cid of group.contactIds) {
        if (cid !== group.ownerId) {
          initial[cid] = true;
        }
      }
    }
    return initial;
  });

  // Child group selection
  const [selectedChildGroups, setSelectedChildGroups] = useState<string[]>(group?.childGroupIds || []);
  const [nameError, setNameError] = useState("");

  const isExisting = !!group?.id;
  const { isEditingField, bindField } = useActiveFieldTracker();

  useEffect(() => {
    if (group) {
      if (!isEditingField("name") && group.name !== undefined) {
        setFormName(group.name || "");
      }
      if (!isEditingField("description") && group.description !== undefined) {
        setFormDesc(group.description || "");
      }
      if (!isEditingField("childGroups") && group.childGroupIds) {
        setSelectedChildGroups(group.childGroupIds);
      }
      if (!isEditingField("members") && group.contactIds) {
        const initial: { [contactId: string]: boolean } = {};
        for (const cid of group.contactIds) {
          if (cid !== group.ownerId) {
            initial[cid] = true;
          }
        }
        setSelectedMembers(initial);
      }
      if (group.photoUrl || group.photoIconUrl) {
        setPhotoUrl(group.photoUrl || group.photoIconUrl || "");
      }
      if (group.photoMediaId) {
        setPhotoMediaId(group.photoMediaId);
      }
    }
  }, [group?.id, group?.name, group?.description, group?.contactIds, group?.childGroupIds, group?.photoUrl, group?.photoIconUrl, group?.photoMediaId, isEditingField]);

  const buildRelationshipMaps = () => {
    const contacts: Record<string, { contactId: string; addedAt: number; addedBy: string; role?: "member" | "admin" }> = {};
    const existingContacts = group?.contacts || {};
    Object.keys(selectedMembers)
      .filter((cid) => selectedMembers[cid])
      .forEach((contactId) => {
        contacts[contactId] = {
          contactId,
          addedAt: existingContacts[contactId]?.addedAt || Date.now(),
          addedBy: existingContacts[contactId]?.addedBy || currentUserId || "",
          role: existingContacts[contactId]?.role || "member",
        };
      });

    const childGroups: Record<string, { groupId: string; addedAt: number; addedBy: string }> = {};
    const existingChildGroups = group?.childGroups || {};
    selectedChildGroups.forEach((groupId) => {
      childGroups[groupId] = {
        groupId,
        addedAt: existingChildGroups[groupId]?.addedAt || Date.now(),
        addedBy: existingChildGroups[groupId]?.addedBy || currentUserId || "",
      };
    });

    return { contacts, childGroups };
  };

  const [selectedTags, setSelectedTags] = useState<Record<string, ITagAssociationMeta>>(group?.tags || {});
  const [selectedMemories, setSelectedMemories] = useState<Record<string, IEntityMemoryMeta>>(group?.memories || {});
  const [selectedMedia, setSelectedMedia] = useState<Record<string, IEntityMediaMeta>>(group?.media || {});
  const [selectedPlaces, setSelectedPlaces] = useState<Record<string, IEntityPlaceMeta>>(group?.places || {});
  const [selectedAnchors, setSelectedAnchors] = useState<Record<string, IEntityAnchorMeta>>(group?.anchors || {});

  useEffect(() => {
    if (group?.tags) {
      setSelectedTags(group.tags);
    }
    if (group?.memories) {
      setSelectedMemories(group.memories);
    }
    if (group?.media) {
      setSelectedMedia(group.media);
    }
    if (group?.places) {
      setSelectedPlaces(group.places);
    }
    if (group?.anchors) {
      setSelectedAnchors(group.anchors);
    }
  }, [group?.tags, group?.memories, group?.media, group?.places, group?.anchors]);

  const autoSave = useAutoSave({
    data: {
      name: formName,
      description: formDesc,
      ...buildRelationshipMaps(),
      tags: selectedTags,
      photoMediaId: photoMediaId || undefined,
      favouriteRating: favouriteRating,
      memories: selectedMemories,
      media: selectedMedia,
      places: selectedPlaces,
      anchors: selectedAnchors,
    },
    enabled: isExisting && canEdit && !!formName.trim(),
    debounceMs: 600,
    onSave: async (data) => {
      await onSave(data);
    },
  });

  const uploadAndSetPhoto = async (source: any) => {
    if (!services) {
      CustomAlert.alert("Error", "Firebase services are not available.");
      return;
    }

    try {
      setUploadingPhoto(true);
      const variants = await createProfileImageVariants(source);
      const cleanPhotoBase64 = variants.photoBase64.replace(/^data:[^;]+;base64,/, "");

      const res = await createMedia(services, {
        base64: cleanPhotoBase64,
        contentType: variants.contentType || "image/jpeg",
        title: `${formName || "Group"} Picture`,
        mediaTarget: "group",
        targetId: group?.id,
        skipAutoMemory: true,
      });

      if (res && res.status && res.media) {
        const newMediaId = res.media.id;
        const newPhotoUrl = res.downloadUrl || res.media.downloadUrl;
        setPhotoMediaId(newMediaId);
        setPhotoUrl(newPhotoUrl);
        if (group?.id && canEdit) {
          onSave({
            name: formName,
            description: formDesc,
            ...buildRelationshipMaps(),
            tags: selectedTags,
            photoMediaId: newMediaId,
          });
        }
      } else {
        CustomAlert.alert("Upload Blocked", res?.error || "This image violates Memories safety policies.");
      }
    } catch (err: any) {
      console.error("Error updating group picture:", err);
      CustomAlert.alert("Upload Error", err.message || "Failed to upload group picture.");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoSelected = async (assets: any[]) => {
    const asset = assets?.[0];
    if (!asset?.uri) return;
    await uploadAndSetPhoto(asset.uri);
  };

  const handlePhotoCaptured = async (file: any) => {
    if (!file) return;
    const source = file.uri ? file.uri : file;
    await uploadAndSetPhoto(source);
  };

  const handleSelectGroupMedia = (media: MediaModel) => {
    if (!media?.id) return;
    setShowMediaSelector(false);
    const newMediaId = media.id;
    const newPhotoUrl = media.iconUrl || media.downloadUrl;
    setPhotoMediaId(newMediaId);
    setPhotoUrl(newPhotoUrl);
    if (group?.id && canEdit) {
      onSave({
        name: formName,
        description: formDesc,
        ...buildRelationshipMaps(),
        tags: selectedTags,
        photoMediaId: newMediaId,
      });
    }
  };

  const handleSave = () => {
    if (!formName.trim()) {
      setNameError("Group name is required");
      return;
    }

    onSave({
      name: formName,
      description: formDesc,
      ...buildRelationshipMaps(),
      tags: selectedTags,
      photoMediaId: photoMediaId || undefined,
      favouriteRating: favouriteRating,
      memories: selectedMemories,
      media: selectedMedia,
      places: selectedPlaces,
      anchors: selectedAnchors,
    });
  };

  const validMemberIds = new Set<string>();
  allGroups.forEach((g) => g.contactIds?.forEach((cid) => validMemberIds.add(cid)));

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.scrollContent}>
      <ThemedView style={styles.section}>
        <View style={styles.avatarWrapper}>
          <View style={styles.avatarContainer}>
            {photoUrl ? (
              <Image
                source={{ uri: photoUrl }}
                style={styles.avatarImage}
                resizeMode="cover"
              />
            ) : (
              <ThemedText style={styles.avatarInitial}>
                {(formName || "👥")[0].toUpperCase()}
              </ThemedText>
            )}
            {canEdit && (
              <UploadMedia
                onMediaSelected={handlePhotoSelected}
                allowsEditing
                aspect={[1, 1]}
                quality={0.9}
                disabled={uploadingPhoto}
                uploading={uploadingPhoto}
                renderTrigger={(onPress, disabled) => (
                  <TouchableOpacity
                    style={styles.avatarBadge}
                    onPress={onPress}
                    disabled={disabled}
                    accessibilityRole="button"
                    accessibilityLabel="Upload group picture"
                  >
                    {uploadingPhoto ? (
                      <ActivityIndicator size="small" color="#FFFFFF" />
                    ) : (
                      <Ionicons name="camera" size={16} color="#FFFFFF" />
                    )}
                  </TouchableOpacity>
                )}
              />
            )}
          </View>

          {canEdit && (
            <View style={styles.photoActionsRow}>
              <UploadMedia
                onMediaSelected={handlePhotoSelected}
                allowsEditing
                aspect={[1, 1]}
                quality={0.9}
                disabled={uploadingPhoto}
                uploading={uploadingPhoto}
                renderTrigger={(onPress, disabled) => (
                  <TouchableOpacity
                    style={[styles.photoActionButton, { borderColor: tintColor }]}
                    onPress={onPress}
                    disabled={disabled}
                    accessibilityRole="button"
                    accessibilityLabel="Choose group photo from library"
                  >
                    <Ionicons name="images-outline" size={14} color={tintColor} style={styles.photoActionIcon} />
                    <ThemedText style={[styles.photoActionText, { color: tintColor }]}>
                      {uploadingPhoto
                        ? "Uploading..."
                        : photoUrl
                        ? "Choose Photo"
                        : "Upload Photo"}
                    </ThemedText>
                  </TouchableOpacity>
                )}
              />

              <CapturePhoto
                onCapture={handlePhotoCaptured}
                disabled={uploadingPhoto}
                uploading={uploadingPhoto}
                initialCameraType="front"
                renderTrigger={(onPress, disabled) => (
                  <TouchableOpacity
                    style={[styles.photoActionButton, { borderColor: tintColor }]}
                    onPress={onPress}
                    disabled={disabled}
                    accessibilityRole="button"
                    accessibilityLabel="Take group photo with camera"
                  >
                    <Ionicons name="camera-outline" size={14} color={tintColor} style={styles.photoActionIcon} />
                    <ThemedText style={[styles.photoActionText, { color: tintColor }]}>
                      Take Photo
                    </ThemedText>
                  </TouchableOpacity>
                )}
              />

              <TouchableOpacity
                style={[styles.photoActionButton, { borderColor: tintColor }]}
                onPress={() => setShowMediaSelector(true)}
                disabled={uploadingPhoto}
                accessibilityRole="button"
                accessibilityLabel="Select group photo from media library"
                testID="group-select-media-btn"
              >
                <Ionicons name="folder-open-outline" size={14} color={tintColor} style={styles.photoActionIcon} />
                <ThemedText style={[styles.photoActionText, { color: tintColor }]}>
                  Select Media
                </ThemedText>
              </TouchableOpacity>
            </View>
          )}
        </View>

        {!canEdit && (
          <View style={styles.roleBadge}>
            <ThemedText style={styles.roleBadgeText}>View Only</ThemedText>
          </View>
        )}

        <ThemedText type="fieldLabel">Group Name</ThemedText>
        <ThemedInput
          {...bindField("name")}
          placeholder="E.g. Family"
          value={formName}
          onChangeText={(val) => {
            setFormName(val);
            if (nameError) setNameError("");
          }}
          editable={canEdit}
          error={nameError}
        />

        <ThemedText type="fieldLabel" style={{ marginTop: 4 }}>Description</ThemedText>
        <CollaborativeEditor
          {...bindField("description")}
          entityId={group?.id}
          entityType="group"
          placeholder="E.g. All my Family members"
          initialContent={formDesc}
          onChange={setFormDesc}
          editable={canEdit}
        />

        <View style={styles.ratingRow}>
          <ThemedText type="fieldLabel">Favourite Rating</ThemedText>
          <ThemedRating
            value={favouriteRating}
            onChange={(val) => {
              setFavouriteRating(val);
            }}
            size={18}
            showLabel={false}
            readOnly={!canEdit}
            style={styles.ratingControl}
          />
        </View>
      </ThemedView>

      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Members</ThemedText>
        <View style={styles.listContainer}>
          {contactsList.filter((c) => c.id !== currentUserId && (canEdit || !!selectedMembers[c.id!])).length > 0 ? (
            contactsList
              .filter((c) => c.id !== currentUserId && (canEdit || !!selectedMembers[c.id!]))
              .map((c) => {
                const isSelected = !!selectedMembers[c.id!];
                return (
                  <TouchableOpacity
                    key={c.id}
                    style={styles.itemRow}
                    onPress={() => {
                      if (!canEdit) return;
                      const next = { ...selectedMembers };
                      if (isSelected) delete next[c.id!];
                      else next[c.id!] = true;
                      setSelectedMembers(next);
                    }}
                    disabled={!canEdit}
                  >
                    <View style={[styles.checkbox, isSelected && styles.checkboxSelected, !canEdit && styles.checkboxDisabled]} />
                    <View style={{ flexDirection: "row", alignItems: "center", gap: 4 }}>
                      <ThemedEntityLink
                        type="contact"
                        id={c.id!}
                        displayName={c.displayName || c.email}
                        variant="subtle"
                        textStyle={styles.itemName}
                      />
                      {c.userId && <Ionicons name="checkmark-circle" size={14} color="#1e8e3e" />}
                    </View>
                  </TouchableOpacity>
                );
              })
          ) : (
            <ThemedText style={styles.emptyInfo}>No members selected.</ThemedText>
          )}
        </View>
      </ThemedView>

      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Child Groups</ThemedText>
        <View style={styles.listContainer}>
          {allGroups.filter((g) => g.id !== group?.id && (canEdit || selectedChildGroups.includes(g.id!))).length > 0 ? (
            allGroups
              .filter((g) => g.id !== group?.id && (canEdit || selectedChildGroups.includes(g.id!)))
              .map((g) => {
                const isSelected = selectedChildGroups.includes(g.id!);
                return (
                  <TouchableOpacity
                    key={g.id}
                    style={styles.itemRow}
                    onPress={() => {
                      if (!canEdit) return;
                      if (isSelected) {
                        setSelectedChildGroups(selectedChildGroups.filter((id) => id !== g.id));
                      } else {
                        setSelectedChildGroups([...selectedChildGroups, g.id!]);
                      }
                    }}
                    disabled={!canEdit}
                  >
                    <View style={[styles.checkbox, isSelected && styles.checkboxSelected, !canEdit && styles.checkboxDisabled]} />
                    <ThemedEntityLink
                      type="group"
                      id={g.id!}
                      displayName={g.name}
                      variant="subtle"
                      textStyle={styles.itemName}
                    />
                  </TouchableOpacity>
                );
              })
          ) : (
            <ThemedText style={styles.emptyInfo}>No child groups selected.</ThemedText>
          )}
        </View>
      </ThemedView>

      {/* Linked Media Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Media</ThemedText>
        <EntityLinkStrip
          entityId={group?.id}
          entityType="group"
          targetType="media"
          links={selectedMedia}
          onLinksChange={setSelectedMedia}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
        />
      </ThemedView>

      {/* Linked Memories Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Memories</ThemedText>
        <EntityLinkStrip
          entityId={group?.id}
          entityType="group"
          targetType="memory"
          links={selectedMemories}
          onLinksChange={setSelectedMemories}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
        />
      </ThemedView>

      {/* Linked Places Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Places</ThemedText>
        <EntityLinkStrip
          entityId={group?.id}
          entityType="group"
          targetType="place"
          links={selectedPlaces}
          onLinksChange={setSelectedPlaces}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
        />
      </ThemedView>

      {/* Linked Anchors Section */}
      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Linked Anchors</ThemedText>
        <EntityLinkStrip
          entityId={group?.id}
          entityType="group"
          targetType="anchor"
          links={selectedAnchors}
          onLinksChange={setSelectedAnchors}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
        />
      </ThemedView>

      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Tags</ThemedText>
        <TagListStrip
          entityId={group?.id}
          entityType="group"
          tags={selectedTags}
          onTagsChange={setSelectedTags}
          canEdit={canEdit}
          isOwner={isOwner}
          currentUserId={resolvedUserId}
        />
      </ThemedView>

      <ThemedView style={styles.section}>
        <ThemedText style={styles.sectionTitle}>Shares</ThemedText>
        <SharingControl
          shares={shares}
          contacts={contactsList}
          groups={allGroups}
          isLoading={isLoadingShares}
          canShare={group?.canShare ?? true}
          canUnshare={group?.canShare ?? true}
          onShare={onShare || (() => {})}
          onUnshare={onUnshare || (() => {})}
        />
      </ThemedView>

      <View style={styles.footer}>
        <View style={styles.leftFooterControls}>
          <ThemedButton title={isExisting || !canEdit ? "Back" : "Cancel"} variant="minimal" onPress={onCancel} />
          {isExisting && <AutoSaveIndicator status={autoSave.status} errorMessage={autoSave.error} />}
        </View>
        <View style={styles.rightFooterControls}>
          {!isExisting && canEdit && (
            <ThemedButton
              title={isSaving ? "Saving..." : submitTitle}
              onPress={handleSave}
              disabled={isSaving}
              loading={isSaving}
              style={styles.actionBtn}
            />
          )}
        </View>
      </View>

      <MediaSelector
        visible={showMediaSelector}
        onClose={() => setShowMediaSelector(false)}
        onSelectSingleMedia={handleSelectGroupMedia}
        onSelectMedia={(items) => {
          if (items.length > 0) {
            handleSelectGroupMedia(items[items.length - 1]);
          }
        }}
        multiSelect={false}
        imagesOnly={true}
        allowedMediaTypes={["image"]}
        title="Select Group Picture"
        testID="group-media-selector"
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scrollContent: {
    padding: 8,
    gap: 8,
  },
  section: {
    paddingVertical: 6,
    paddingHorizontal: 8,
    borderRadius: 0,
    backgroundColor: "rgba(150, 150, 150, 0.05)",
    borderWidth: 1,
    borderColor: "rgba(0, 0, 0, 0.05)",
  },
  roleBadge: {
    alignSelf: "flex-start",
    backgroundColor: "rgba(0,0,0,0.05)",
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 0,
    marginBottom: 4,
  },
  roleBadgeText: { fontSize: 11, fontWeight: "bold", textTransform: "uppercase", opacity: 0.6 },
  sectionTitle: {
    fontSize: 13,
    fontWeight: "bold",
    marginBottom: 4,
  },
  listContainer: {
    maxHeight: 300,
    gap: 0,
  },
  itemRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 4,
    borderBottomWidth: 1,
    borderBottomColor: "rgba(0, 0, 0, 0.03)",
  },
  checkbox: {
    width: 18,
    height: 18,
    borderRadius: 0,
    borderWidth: 1.5,
    borderColor: "#ccc",
    marginRight: 8,
  },
  checkboxSelected: {
    backgroundColor: "#4A90E2",
    borderColor: "#4A90E2",
  },
  checkboxDisabled: {
    opacity: 0.5,
  },
  itemName: {
    fontSize: 13,
  },
  emptyInfo: {
    fontSize: 12,
    fontStyle: "italic",
    opacity: 0.5,
    paddingVertical: 2,
  },
  footer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    marginTop: 16,
    paddingBottom: 24,
  },
  leftFooterControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  rightFooterControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  actionBtn: {
    minWidth: 140,
  },
  avatarWrapper: {
    alignItems: "center",
    marginBottom: 12,
  },
  avatarContainer: {
    width: 90,
    height: 90,
    borderRadius: 45,
    backgroundColor: "rgba(100, 116, 139, 0.2)",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  avatarImage: {
    width: 90,
    height: 90,
    borderRadius: 45,
    overflow: "hidden",
  },
  avatarInitial: {
    fontSize: 32,
    fontWeight: "bold",
    opacity: 0.8,
  },
  avatarBadge: {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "#2563EB",
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 2,
    borderColor: "#FFFFFF",
    elevation: 3,
  },
  photoActionsRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: 8,
    marginTop: 8,
  },
  photoActionButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 5,
    borderWidth: 1,
    borderRadius: 0,
  },
  photoActionIcon: {
    marginRight: 4,
  },
  photoActionText: {
    fontSize: 12,
    fontWeight: "600",
  },
  ratingRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "nowrap",
    marginTop: 12,
    marginBottom: 6,
    gap: 12,
  },
  ratingLabel: {
    marginBottom: 0,
  },
  ratingControl: {
    marginVertical: 0,
  },
});
